import { compact, sortBy } from "lodash";
import moment from "moment";
import { useMemo, useState } from "react";
import {
  AssessmentDeliveryState,
  AttendeeRole,
  AttendeeStatus,
  GetAssessmentDeliveryQuery,
} from "types/graphql-schema";

import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import SelectOrCreate1on1Button from "@apps/meeting-dialog/select-or-create-1-on-1-button";
import { getProgramUrl } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import AppLabel from "@components/draft-label/app-label";
import DraftLabel from "@components/draft-label/draft-label";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertNonNull,
  formatMeetingTimes,
  getUrl,
  getUrlUsingTypename,
  removeCurrentYear,
} from "@helpers/helpers";

import RelatedSubmissions from "../assessment/related-submissions/related-submissions";
import { RelatedSubmissionsLocation } from "../assessment/related-submissions/types";

const AssessmentDeliveryHeader = ({
  assessmentDeliveryData,
  viewAsDropdown,
  avatarReplacement,
  defaultCollapsed,
}: {
  assessmentDeliveryData?: GetAssessmentDeliveryQuery;
  viewAsDropdown?: JSX.Element;
  avatarReplacement?: JSX.Element;
  defaultCollapsed?: boolean;
}) => {
  const currentUser = currentUserVar();
  const label = useLabel();
  const [isOpenMeetingDialog, setIsOpenMeetingDialog] = useState(false);

  const assessmentDelivery = useMemo(
    () =>
      assessmentDeliveryData?.assessmentDelivery
        ? assessmentDeliveryData.assessmentDelivery
        : null,
    [assessmentDeliveryData],
  );
  const program = useMemo(
    () => (assessmentDelivery ? assessmentDelivery.complianceProgram : null),
    [assessmentDelivery],
  );
  const canReadProgramDetails = useMemo(
    () => program?.canReadDetails.permission,
    [program],
  );

  const target = useMemo(
    () =>
      assessmentDelivery?.complianceProgram &&
      !assessmentDelivery.complianceProgram.engagementSurveyTemplate
        ? assertNonNull(assessmentDelivery.target)
        : null,
    [assessmentDelivery],
  );

  const relatedMeetings = useMemo(() => {
    return sortBy(
      assessmentDelivery?.relatedMeetings
        ? assertEdgesNonNull(assessmentDelivery.relatedMeetings)
        : [],
      "draft",
    );
  }, [assessmentDelivery]);
  const firstRelatedMeeting = useMemo(() => {
    return relatedMeetings[0];
  }, [relatedMeetings]);
  const firstRelatedMeetingTitle =
    firstRelatedMeeting &&
    firstRelatedMeeting.startDatetime &&
    firstRelatedMeeting.endDatetime
      ? `${removeCurrentYear(
          moment(firstRelatedMeeting.startDatetime).format("MMMM D, YYYY"),
        )}, ${formatMeetingTimes(firstRelatedMeeting)}`
      : firstRelatedMeeting
        ? firstRelatedMeeting.title
        : `Draft Meeting`;

  useDocumentTitle(
    `${label("review", { capitalize: true })} ${target?.name}: ${
      program?.title
    }`,
  );

  if (!assessmentDeliveryData) {
    return null;
  }

  const leftCellClassName = "py-2 font-medium w-[200px]";
  const rightCellClassName = "py-2";
  const isDraft =
    assessmentDelivery?.state !== AssessmentDeliveryState.Delivered;

  const requiredTopicTemplates = assessmentDelivery?.complianceProgram
    ?.requiredTopicTemplates
    ? assertEdgesNonNull(
        assessmentDelivery.complianceProgram.requiredTopicTemplates,
      )
    : [];
  const requiredTopicTemplate = requiredTopicTemplates[0];

  const managers = assessmentDelivery?.target?.managers
    ? assertEdgesNonNull(assessmentDelivery.target.managers)
    : [];
  const targetManager = managers[0];
  const currentUserIsManager = managers.some(
    (manager) => currentUser?.id === manager?.id,
  );
  const canEditMeeting =
    currentUser?.id === assessmentDelivery?.target?.id || currentUserIsManager;

  return (
    <div className={classNames("text-sm pb-6 mb-12 border-b")}>
      <div className="flex flex-col gap-6 w-full mx-auto max-w-screen-lg">
        <div className="flex items-center gap-2 justify-between">
          {avatarReplacement ? (
            avatarReplacement
          ) : (
            <div className="flex items-center gap-2">
              <Avatar user={target} size={8} />
              {assessmentDelivery && (
                <AppLink
                  to={getUrlUsingTypename(assessmentDelivery)}
                  className="text-2xl font-bold text-gray-800 hover:underline"
                >{`${label("review", {
                  capitalize: true,
                })}: ${target?.name}`}</AppLink>
              )}
            </div>
          )}
          {viewAsDropdown}
        </div>

        <div>
          <Layout.MainSubSection
            title={`${label("review", { capitalize: true })} Details`}
            collapsible
            defaultIsExpanded={!defaultCollapsed}
          >
            <table className="text-gray-600 w-full">
              <tbody>
                <tr>
                  <td className={leftCellClassName}>Program</td>
                  {canReadProgramDetails ? (
                    <td
                      className={classNames(
                        rightCellClassName,
                        "py-0", // to compensate for button padding
                      )}
                    >
                      <Button
                        to={getProgramUrl(assertNonNull(program).id, {
                          isSurvey:
                            !!assertNonNull(program).engagementSurveyTemplate,
                        })}
                        theme={buttonTheme.text}
                        leftNegativeMargin
                      >
                        <TopicflowIcon
                          type="program"
                          className="shrink-0 w-4 h-4"
                        />
                        {program?.title}
                      </Button>
                    </td>
                  ) : (
                    <td
                      className={classNames(
                        rightCellClassName,
                        "flex items-center gap-1",
                      )}
                    >
                      <TopicflowIcon
                        type="program"
                        className="shrink-0 w-4 h-4 text-gray-400"
                      />
                      {program?.title}
                    </td>
                  )}
                </tr>
                <tr>
                  <td className={leftCellClassName}>Subject</td>
                  <td className={rightCellClassName}>
                    <div className="flex items-center">
                      <Avatar className="mr-1" user={target} size="5" />{" "}
                      {target?.name}
                    </div>
                  </td>
                </tr>
                {target?.organizationSpecificInfo?.startDate && (
                  <tr>
                    <td className={leftCellClassName}>Subject's Start Date</td>
                    <td className={rightCellClassName}>
                      {moment(
                        target?.organizationSpecificInfo?.startDate,
                      ).format("MMM D, YYYY")}
                    </td>
                  </tr>
                )}
                {assessmentDelivery?.targetPosition && (
                  <tr>
                    <td className={leftCellClassName}>Position</td>
                    <td className={rightCellClassName}>
                      {assessmentDelivery.targetPosition}
                    </td>
                  </tr>
                )}

                <tr>
                  <td className={leftCellClassName}>Program period</td>
                  <td className={rightCellClassName}>
                    {program?.periodStartDate && program?.periodEndDate
                      ? `${moment(program.periodStartDate).format(
                          "MMM D, YYYY",
                        )} - ${moment(program.periodEndDate).format(
                          "MMM D, YYYY",
                        )}`
                      : "Not set"}
                  </td>
                </tr>
                <tr>
                  <td className={leftCellClassName}>Due date</td>
                  <td className={rightCellClassName}>
                    {moment(assessmentDelivery?.dueDate).format("MMM D, YYYY")}
                  </td>
                </tr>

                {!isDraft && (
                  <tr>
                    <td className={leftCellClassName}>Delivery</td>
                    <td className={rightCellClassName}>
                      <div>
                        {assessmentDelivery.deliveryDatetime
                          ? `Delivered by ${
                              assessmentDelivery.creator?.name
                            } on ${moment(
                              assessmentDelivery.deliveryDatetime,
                            ).format("MMM D, YYYY @ h:mma")}`
                          : "Immediate access"}
                      </div>
                    </td>
                  </tr>
                )}

                {requiredTopicTemplate && (
                  <tr>
                    <td className={leftCellClassName}>
                      {label("review", { capitalize: true })} Meeting
                    </td>
                    <td className={rightCellClassName}>
                      {firstRelatedMeeting &&
                      firstRelatedMeeting.startDatetime &&
                      !firstRelatedMeeting.draft ? (
                        <Button
                          theme={buttonTheme.text}
                          leftNegativeMargin
                          to={getUrl({
                            meetingId: firstRelatedMeeting.id,
                            meetingGroupId: firstRelatedMeeting.meetingGroupId,
                          })}
                        >
                          <TopicflowIcon
                            type="meeting"
                            className="w-4 h-4 text-gray-400 shrink-0"
                          />
                          {firstRelatedMeetingTitle}
                          {firstRelatedMeeting.isFinalized ? (
                            <AppLabel
                              className="ml-1 bg-emerald-100 text-emerald-600"
                              label="Finalized"
                            />
                          ) : (
                            <AppLabel className="ml-1" label="Not Finalized" />
                          )}
                        </Button>
                      ) : firstRelatedMeeting?.draft ? (
                        <div className="flex items-center gap-2">
                          <Button
                            theme={buttonTheme.text}
                            leftNegativeMargin
                            to={getUrl({
                              meetingId: firstRelatedMeeting.id,
                              meetingGroupId:
                                firstRelatedMeeting.meetingGroupId,
                            })}
                          >
                            <TopicflowIcon
                              type="meeting"
                              className="w-4 h-4 text-gray-400 shrink-0"
                            />
                            {firstRelatedMeetingTitle}
                          </Button>
                          <button
                            className="flex hover:opacity-90"
                            onClick={() => setIsOpenMeetingDialog(true)}
                          >
                            <DraftLabel />
                          </button>
                        </div>
                      ) : canEditMeeting ? (
                        <>
                          <Button
                            theme={buttonTheme.text}
                            leftNegativeMargin
                            onClick={() => setIsOpenMeetingDialog(true)}
                          >
                            <TopicflowIcon
                              type="meeting"
                              className="w-4 h-4 text-gray-400 shrink-0"
                            />
                            Unscheduled
                          </Button>
                        </>
                      ) : (
                        <div className="flex items-center gap-1">
                          <TopicflowIcon
                            type="meeting"
                            className="w-4 h-4 text-gray-400 shrink-0"
                          />
                          Unscheduled
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Layout.MainSubSection>

          {isOpenMeetingDialog && (
            <MeetingDialog
              onClose={() => setIsOpenMeetingDialog(false)}
              refetchQueries={["getAssessmentDelivery"]}
              meetingGroupId={
                firstRelatedMeeting
                  ? firstRelatedMeeting.meetingGroupId
                  : undefined
              }
              meetingId={
                firstRelatedMeeting ? firstRelatedMeeting.id : undefined
              }
              formOptions={
                firstRelatedMeeting
                  ? {}
                  : {
                      title: `${label("review", {
                        capitalize: true,
                      })} Meeting`,
                      templateId: requiredTopicTemplate?.id,
                      isFormalOneonone: true,
                      facilitatorId: targetManager?.id,
                      attendees: compact([
                        assessmentDelivery?.target && {
                          ...assessmentDelivery.target,
                          role: AttendeeRole.Required,
                          participantStatus: AttendeeStatus.NotResponded,
                        },
                        targetManager && {
                          ...targetManager,
                          role: AttendeeRole.Required,
                          participantStatus: AttendeeStatus.NotResponded,
                        },
                      ]),
                    }
              }
            />
          )}

          {assessmentDelivery &&
            target &&
            assessmentDelivery.complianceProgram && (
              <RelatedSubmissions
                target={target}
                complianceProgramId={assessmentDelivery.complianceProgram.id}
                location={RelatedSubmissionsLocation.DELIVERY}
                defaultCollapsed={defaultCollapsed}
              />
            )}

          {canEditMeeting &&
            requiredTopicTemplate &&
            !firstRelatedMeeting?.id &&
            targetManager &&
            target && (
              <div className="mt-4 flex items-center gap-6 justify-between pl-4 pr-6 py-4 bg-blue-50 rounded-lg tf-print-display-none">
                <div className="flex items-start gap-2">
                  <TopicflowIcon
                    type="info"
                    className="w-5 h-5 text-blue-400 shrink-0"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Schedule a meeting for {target?.name}'s{" "}
                      {label("review", { capitalize: true })}!
                    </div>
                    <div className="mt-1 text-sm text-gray-800">
                      Prepare to go over {target?.name}'s {label("review")} by
                      creating a new {label("1-on-1")} meeting.
                    </div>
                  </div>
                </div>
                <div>
                  <SelectOrCreate1on1Button
                    buttonProps={{
                      className: "shrink-0 whitespace-nowrap",
                      theme: buttonTheme.lightBlue,
                      text: `Schedule ${label("review", {
                        titleCase: true,
                      })} Meeting`,
                      onClick: () => setIsOpenMeetingDialog(true),
                    }}
                    matchingOneonone={firstRelatedMeeting}
                    target={target}
                    requiredTopicTemplateId={requiredTopicTemplate.id}
                    facilitator={targetManager}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentDeliveryHeader;
